<template lang="pug">
.mainwrapper
  #app
    router-view
    ToastGlobal(v-if='globalMessage', :message='globalMessage')
</template>

<script>
import i18n from '@/locales/i18n';
import { mapGetters, mapActions } from 'vuex';
import router from '@/router';
import ApiService from '@/services/api.service';
import UserService from '@/services/user.service';
import SocketService from '@/services/socket.service';
import { TokenService, LanguageService, EnviromentService } from '@/services/storage.service';
import { getErrorId } from '@/services/error.service';
import ToastGlobal from '@/components/ToastGlobal.vue';
import { consoleError, consoleSuccess, consoleInfo } from '@/utils/console.utils';
import { LANGUAGE, IS_MOBILE } from '@/app.config';

export default {
  components: { ToastGlobal },
  data() {
    return {
      isOnline: true,
      isPause: false,
      isLoading: false,
      globalMessage: null,
      isMobile: IS_MOBILE,
    };
  },
  computed: {
    ...mapGetters({
      globalError: 'globalModule/isError',
      isDemo: 'globalModule/isDemo',
    }),
  },
  watch: {
    /**
     * Maneja errores globales
     */
    globalError(error) {
      /**
       * Cuando limpiamos el error global, también se vueleve a ejecutar el watch
       * comprobamos si existe un error
       */
      if (!error.errorId) {
        this.globalMessage = null;
        return;
      }

      /**
       * Imprime por consola los datos del error
       */
      consoleError(`${error.errorId} - ${error.description}`);

      /**
       * Comprueba el tipo de error
       */
      switch (error.errorId) {
        /**
         * El backend ha caido
         */
        case 'backendDown':
          this.globalMessage = i18n.t('general.backendDown');
          break;

        /**
         * No hay conexión a internet
         */
        case 'networkError':
          this.isOnline = false;
          this.globalMessage = i18n.t('general.offline');
          break;

        /**
         * La sesión ha sido cerrada desde otro dispositivo
         */
        case 'sessionClosed':
          UserService.cleanData();
          router.push({ name: 'login', query: { reaseon: 'sessionClosed' } });
          this.clearError();
          break;

        /**
         * La sesión ha sido cerrada desde otro dispositivo
         */
        case 'tokenNotFound':
          UserService.cleanData();
          router.push({ name: 'login', query: { reaseon: 'tokenNotFound' } });
          this.clearError();
          break;

        /**
         * Error al conectar con el socket de una instalación o
         * error al validar los datos de una instalación
         * la aplicación debe seguir funcionando, aunque no se mostrarán datos de esa instalación
         */
        case 'invalidInstallation':
          this.globalMessage = i18n.t('general.installationError');
          setTimeout(() => {
            this.clearError();
          }, 3000);
          break;

        /**
         * Error al validar los datos de un dispositivo
         * la aplicación debe seguir funcionando, aunque no se mostrarán datos de esa instalación
         */
        case 'invalidDevice':
          this.globalMessage = i18n.t('general.deviceError');
          setTimeout(() => {
            this.clearError();
          }, 3000);
          break;

        /**
         * Error al validar los datos de una programación
         * la aplicación debe seguir funcionando, aunque no se mostrarán datos de esta programación
         */
        case 'invalidSchedule':
          this.globalMessage = i18n.t('general.scheduleError');
          setTimeout(() => {
            this.clearError();
          }, 3000);
          break;

        /**
         * Error al validar los datos de una programación
         * la aplicación debe seguir funcionando, aunque no se mostrarán datos de esta programación
         */
        case 'invalidScene':
          this.globalMessage = i18n.t('general.sceneError');
          setTimeout(() => {
            this.clearError();
          }, 3000);
          break;

        /**
         * Ocurrió cualquier otro error que impide el funcionamiento de la aplicación
         */
        default:
          this.globalMessage = i18n.t('general.errors.default.message');
          break;
      }
    },
  },
  methods: {
    ...mapActions({
      clearError: 'globalModule/cleanError',
      setError: 'globalModule/setError',
      setDemo: 'globalModule/setDemo',
      setLanguage: 'globalModule/setLanguage',
      setAppMode: 'globalModule/setAppMode',
    }),

    /**
     * Comprueba si la sesión del usuario sigue activa y obtine sus datos.
     * Si su tokengetErrorIdoken han caducado, o si el usuario había cerrado sesión
     * la función getErrorId lo redirigen a la vista login,
     * si ocurre cualquier otro error, también lo redirijo a la vista login
     */
    async checkIsLogin() {
      try {
        // Si estamos en modo demo, lo desactivo y redirijo al login
        if (this.isDemo) {
          this.setDemo(false);
          this.$router.push({ name: 'login' });
        }

        // Compruebo si existe un token almacenado en el localStorage,
        // o si hay alguna petición en curso
        const token = TokenService.getToken();

        if (!token || this.isLoading || this.isPause) return;

        consoleInfo('checkIsLogin');

        // Muestro el loader
        this.$toast.loading({
          duration: 0,
          mask: true,
          message: i18n.t('general.loading'),
        });

        // Inicializo valores
        this.isLoading = true;
        this.clearError();

        // Configuro el header del Api
        ApiService.setHeader(token);

        // Obtengo los datos del usuario y conecto los sockets
        await UserService.getUserData();

        // Si he recibido los datos implica que la aplicación está online
        if (this.globalError === 'networkError') this.clearError();
        this.isOnline = true;
        // Fix loading issue onResume in thirdParty view
        if (this.$route.name !== 'devices') {
          this.$toast.clear();
        }
      } catch (error) {
        const authError = getErrorId(error);

        this.$toast.clear();

        // Si comprobamos si el usuario está logueado y devuelve 'userNotExist'
        // implica que el usuario ha eliminado su cuenta desde otro dispositivo
        // y lo redirijo a la vista login
        if (authError === 'userNotExist') {
          this.$router.push(this.$route.query.redirect || { name: 'login' });
        } else if (authError === 'invalidUser') {
          this.setError(['invalidUser', 'Error al obtener los datos del usuario']);
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Evento cuando la aplicación recupera la conexión a internet
     * comprueba si el usuario está logueado
     *
     * IMPORTANTE!
     * Este evento solo se lanzará si el estado previo de la aplicación era offline
     * y la aplicación no está en reposo, si la aplicación se inicia con conexión,
     * este evento no se lanzará
     */
    onOnline() {
      if (this.isOnline) return;
      if (this.isPause) return;

      consoleSuccess('The app has recovered the internet connection.');

      this.clearError();

      this.isOnline = true;
      this.isLoading = false;

      this.checkIsLogin();
      // Prevent to clear the loading when onOnline in thirdParty view
      this.$toast.clear();
    },

    /**
     * Evento cuando la aplicación pierde la conexión a internet
     * muestra un mensajde de error
     *
     * IMPORTANTE!
     * Este evento solo se lanzará si el estado previo de la aplicación era online
     * y la aplicación no está en reposo, si la aplicación se inicia con conexión,
     * este evento no se lanzará
     */
    onOffline() {
      if (!this.isOnline) return;
      if (this.isPause) return;

      consoleError('The app has losed the internet connection.');

      SocketService.disconnectAllSocket();
      window.localStorage.setItem('devicesError', false);

      this.$toast.clear();
      this.setError('networkError');

      this.isOnline = false;
    },

    /**
     * Evento cuando la aplicación pasa a reposo
     */
    onPause() {
      consoleInfo('The app is paused.');

      this.isPause = true;
      SocketService.disconnectAllSocket();
      window.localStorage.setItem('devicesError', false);
    },

    /**
     * Evento cuando la aplicación vuelve del reposo
     * comprueba si la aplicación está online
     *
     * IMPORTANTE!
     * Este evento solo se lanzará si la aplicación estaba en reposo
     * si la aplicación se inicia despues de haberse cerrado, este evento no se lanzará
     */
    onResume() {
      consoleSuccess('The app is resume.');
      const appResumeEvent = new CustomEvent('AppResume');

      this.isPause = false;
      this.isLoading = false;

      this.checkIsLogin();
      document.dispatchEvent(appResumeEvent); // Lanzamos el evento personalizado
    },

    disableBackButton(event) {
      event.preventDefault();
    },

    onReady() {
      consoleSuccess('Onready...');
      // Add listeners
      document.addEventListener('offline', this.onOffline, false);
      document.addEventListener('online', this.onOnline, false);
      document.addEventListener('pause', this.onPause, false);
      document.addEventListener('resume', this.onResume, false);
      document.addEventListener('backbutton', this.disableBackButton, false);

      this.initApp();
    },

    initApp() {
      let language = LanguageService.getLanguage();

      if (!language) {
        const navigatorLanguage = navigator.language.split('-');
        const defaultLanguage = LANGUAGE.DEFAULT;
        language = navigatorLanguage[0] || defaultLanguage || 'en';
      }

      this.setLanguage(language);

      // Inicializon el modo en que se encuentra la app
      const mode = EnviromentService.getEnviroment() || 'DEV';
      this.setAppMode(mode);

      // Init devices Error
      window.localStorage.setItem('devicesError', false);

      this.checkIsLogin();
    },
  },

  created() {
    consoleSuccess('Create app...');
    if (!this.isMobile) {
      this.initApp();
    }
    document.addEventListener('deviceready', this.onReady, false);
  },

  destroyed() {
    SocketService.disconnectAllSocket();
    window.localStorage.setItem('devicesError', false);

    document.removeEventListener('online', this.onOnline);
    document.removeEventListener('offline', this.onOffline);
    document.removeEventListener('resume', this.onResume);
    document.removeEventListener('pause', this.onPause);
    document.removeEventListener('backbutton', this.disableBackButton);
  },
};
</script>
